import { collection, onSnapshot, query, doc, setDoc, increment } from 'firebase/firestore';
import { db } from './firebase';

let hasAttemptedSeed = false;

/**
 * Seed initial sample student AI activity if Firestore is currently empty,
 * ensuring the dashboard immediately renders live, dynamic data.
 */
export const seedInitialAiData = async () => {
  if (hasAttemptedSeed) return;
  hasAttemptedSeed = true;

  try {
    const today = new Date().toISOString().split('T')[0];
    const billingMonth = new Date().toISOString().slice(0, 7);

    // 1. Initial Student Budgets ($0.30 cap specification)
    const sampleBudgets = [
      {
        userId: 'student_kunle_ss1',
        subjectId: 'mathematics',
        billingMonth,
        totalSpentUSD: 0.1824,
        budgetLimitUSD: 0.30,
        totalTokens: 52140,
        promptTokens: 38200,
        candidateTokens: 13940,
        interactionCount: 14,
        isNearCap: false,
        isCapped: false,
        lastModelUsed: 'gemini-1.5-flash',
        lastAction: 'ask_teacher',
        lastInteractionAt: new Date(Date.now() - 15 * 60000).toISOString(),
      },
      {
        userId: 'student_chioma_ss1',
        subjectId: 'physics',
        billingMonth,
        totalSpentUSD: 0.2685,
        budgetLimitUSD: 0.30,
        totalTokens: 76800,
        promptTokens: 56100,
        candidateTokens: 20700,
        interactionCount: 22,
        isNearCap: true,
        isCapped: false,
        lastModelUsed: 'gemini-2.0-flash',
        lastAction: 'explain_again',
        lastInteractionAt: new Date(Date.now() - 42 * 60000).toISOString(),
      },
      {
        userId: 'student_ibrahim_ss2',
        subjectId: 'english',
        billingMonth,
        totalSpentUSD: 0.3000,
        budgetLimitUSD: 0.30,
        totalTokens: 91400,
        promptTokens: 68000,
        candidateTokens: 23400,
        interactionCount: 28,
        isNearCap: false,
        isCapped: true,
        lastModelUsed: 'gemini-1.5-pro',
        lastAction: 'teach_me',
        lastInteractionAt: new Date(Date.now() - 110 * 60000).toISOString(),
      },
      {
        userId: 'student_blessing_ss1',
        subjectId: 'chemistry',
        billingMonth,
        totalSpentUSD: 0.1140,
        budgetLimitUSD: 0.30,
        totalTokens: 32600,
        promptTokens: 24000,
        candidateTokens: 8600,
        interactionCount: 9,
        isNearCap: false,
        isCapped: false,
        lastModelUsed: 'gemini-1.5-flash',
        lastAction: 'ask_teacher',
        lastInteractionAt: new Date(Date.now() - 180 * 60000).toISOString(),
      },
      {
        userId: 'student_tunde_ss2',
        subjectId: 'biology',
        billingMonth,
        totalSpentUSD: 0.0810,
        budgetLimitUSD: 0.30,
        totalTokens: 23150,
        promptTokens: 17200,
        candidateTokens: 5950,
        interactionCount: 6,
        isNearCap: false,
        isCapped: false,
        lastModelUsed: 'gemini-1.5-flash',
        lastAction: 'give_hint',
        lastInteractionAt: new Date(Date.now() - 320 * 60000).toISOString(),
      },
    ];

    for (const b of sampleBudgets) {
      const docId = `${b.userId}_${b.subjectId}_${b.billingMonth}`;
      await setDoc(doc(db, 'student_ai_budgets', docId), b, { merge: true });
    }

    // 2. Initial Daily AI Usage Records
    const sampleUsage = [
      {
        id: `student_kunle_ss1_${today}`,
        userId: 'student_kunle_ss1',
        date: today,
        count: 14,
        totalTokens: 52140,
        estimatedCostUSD: 0.1824,
        isSubscribed: true,
        lastUsedAt: new Date(Date.now() - 15 * 60000).toISOString(),
      },
      {
        id: `student_chioma_ss1_${today}`,
        userId: 'student_chioma_ss1',
        date: today,
        count: 22,
        totalTokens: 76800,
        estimatedCostUSD: 0.2685,
        isSubscribed: true,
        lastUsedAt: new Date(Date.now() - 42 * 60000).toISOString(),
      },
      {
        id: `student_ibrahim_ss2_${today}`,
        userId: 'student_ibrahim_ss2',
        date: today,
        count: 28,
        totalTokens: 91400,
        estimatedCostUSD: 0.3000,
        isSubscribed: true,
        lastUsedAt: new Date(Date.now() - 110 * 60000).toISOString(),
      },
      {
        id: `student_blessing_ss1_${today}`,
        userId: 'student_blessing_ss1',
        date: today,
        count: 9,
        totalTokens: 32600,
        estimatedCostUSD: 0.1140,
        isSubscribed: true,
        lastUsedAt: new Date(Date.now() - 180 * 60000).toISOString(),
      },
      {
        id: `student_tunde_ss2_${today}`,
        userId: 'student_tunde_ss2',
        date: today,
        count: 6,
        totalTokens: 23150,
        estimatedCostUSD: 0.0810,
        isSubscribed: false,
        lastUsedAt: new Date(Date.now() - 320 * 60000).toISOString(),
      },
    ];

    for (const u of sampleUsage) {
      await setDoc(doc(db, 'ai_usage', u.id), u, { merge: true });
    }

    // 3. Initial Topic Mastery Records
    const sampleMastery = [
      { id: 'student_kunle_ss1_ss1_math_number_bases', userId: 'student_kunle_ss1', topicId: 'ss1_math_number_bases', masteryScore: 85, currentStep: 3, totalSteps: 3 },
      { id: 'student_chioma_ss1_ss1_math_modular_arithmetic', userId: 'student_chioma_ss1', topicId: 'ss1_math_modular_arithmetic', masteryScore: 70, currentStep: 2, totalSteps: 3 },
      { id: 'student_ibrahim_ss2_ss1_math_indices', userId: 'student_ibrahim_ss2', topicId: 'ss1_math_indices', masteryScore: 92, currentStep: 3, totalSteps: 3 },
      { id: 'student_blessing_ss1_ss1_math_logarithms', userId: 'student_blessing_ss1', topicId: 'ss1_math_logarithms', masteryScore: 60, currentStep: 2, totalSteps: 3 },
      { id: 'student_tunde_ss2_ss1_math_sets', userId: 'student_tunde_ss2', topicId: 'ss1_math_sets', masteryScore: 78, currentStep: 3, totalSteps: 3 },
    ];

    for (const m of sampleMastery) {
      await setDoc(doc(db, 'student_mastery', m.id), m, { merge: true });
    }
  } catch (err) {
    console.warn('Initial AI data seeding skipped or failed:', err.message);
  }
};

/**
 * Simulate a live student AI question (increments Firestore in real-time)
 */
export const simulateLiveAiQuestion = async () => {
  const billingMonth = new Date().toISOString().slice(0, 7);
  const today = new Date().toISOString().split('T')[0];
  const docId = `student_kunle_ss1_mathematics_${billingMonth}`;
  const usageDocId = `student_kunle_ss1_${today}`;

  const budgetRef = doc(db, 'student_ai_budgets', docId);
  const usageRef = doc(db, 'ai_usage', usageDocId);

  // Add 1 interaction, ~480 tokens, ~$0.0018 to Adebayo Kunle
  await setDoc(
    budgetRef,
    {
      totalSpentUSD: increment(0.0018),
      totalTokens: increment(480),
      promptTokens: increment(360),
      candidateTokens: increment(120),
      interactionCount: increment(1),
      lastInteractionAt: new Date().toISOString(),
    },
    { merge: true }
  );

  await setDoc(
    usageRef,
    {
      count: increment(1),
      totalTokens: increment(480),
      estimatedCostUSD: increment(0.0018),
      lastUsedAt: new Date().toISOString(),
    },
    { merge: true }
  );
};

/**
 * Subscribe to AI Usage records in real-time
 */
export const subscribeToAiUsage = (callback) => {
  const q = query(collection(db, 'ai_usage'));

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        seedInitialAiData();
      }

      let totalInteractions = 0;
      const uniqueStudents = new Set();
      let subscribedInteractions = 0;
      let freeInteractions = 0;
      let totalTokensSum = 0;
      let totalCostSum = 0;
      const dateMap = {};

      const usageRecords = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const count = Number(data.count) || 0;
        const tokens = Number(data.totalTokens) || count * 380;
        const cost = Number(data.estimatedCostUSD) || (tokens * 0.00000015);

        totalInteractions += count;
        totalTokensSum += tokens;
        totalCostSum += cost;

        if (data.userId) {
          uniqueStudents.add(data.userId);
        }

        if (data.isSubscribed) {
          subscribedInteractions += count;
        } else {
          freeInteractions += count;
        }

        const dateKey = data.date || (data.lastUsedAt ? data.lastUsedAt.split('T')[0] : 'Today');
        dateMap[dateKey] = (dateMap[dateKey] || 0) + count;

        return {
          id: docSnap.id,
          ...data,
          count,
          totalTokens: tokens,
        };
      });

      const estimatedCost = totalCostSum.toFixed(4);

      // Format chart data sorted by date
      const dailyChart = Object.entries(dateMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, count]) => ({
          date: date.length > 5 ? date.substring(5) : date,
          fullDate: date,
          requests: count,
          tokens: count * 380,
        }));

      callback({
        totalInteractions,
        activeStudents: uniqueStudents.size,
        subscribedInteractions,
        freeInteractions,
        estimatedTokens: totalTokensSum,
        estimatedCost,
        dailyChart: dailyChart.length > 0 ? dailyChart : [
          { date: 'Today', requests: totalInteractions, tokens: totalTokensSum }
        ],
        records: usageRecords,
      });
    },
    (err) => {
      console.error('Error listening to ai_usage:', err);
      callback({
        totalInteractions: 0,
        activeStudents: 0,
        subscribedInteractions: 0,
        freeInteractions: 0,
        estimatedTokens: 0,
        estimatedCost: '0.0000',
        dailyChart: [],
        records: [],
      });
    }
  );
};

/**
 * Subscribe to Student Mastery records in real-time
 */
export const subscribeToStudentMastery = (callback) => {
  const q = query(collection(db, 'student_mastery'));

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        seedInitialAiData();
      }

      const topicStats = {};
      let totalScoreSum = 0;
      let totalCount = 0;

      const topicTitleMap = {
        ss1_math_number_bases: 'Number Bases',
        ss1_math_modular_arithmetic: 'Modular Arithmetic',
        ss1_math_indices: 'Indices',
        ss1_math_logarithms: 'Logarithms',
        ss1_math_sets: 'Sets & Venn',
      };

      const masteryRecords = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const topicId = data.topicId || 'general';
        const score = Number(data.masteryScore) || 0;

        totalScoreSum += score;
        totalCount++;

        if (!topicStats[topicId]) {
          topicStats[topicId] = {
            topicId,
            title: topicTitleMap[topicId] || topicId.replace(/_/g, ' '),
            scores: [],
            attempts: 0,
            mastered: 0,
          };
        }

        topicStats[topicId].scores.push(score);
        topicStats[topicId].attempts++;
        if (score >= 70) {
          topicStats[topicId].mastered++;
        }

        return {
          id: docSnap.id,
          ...data,
        };
      });

      const averageMastery = totalCount > 0 ? Math.round(totalScoreSum / totalCount) : 0;

      const topicChart = Object.values(topicStats).map((t) => {
        const avg = Math.round(t.scores.reduce((a, b) => a + b, 0) / t.scores.length);
        return {
          name: t.title,
          avgMastery: avg,
          students: t.attempts,
          mastered: t.mastered,
        };
      });

      callback({
        averageMastery,
        totalTrackedTopics: totalCount,
        topicChart,
        records: masteryRecords,
      });
    },
    (err) => {
      console.error('Error listening to student_mastery:', err);
      callback({
        averageMastery: 0,
        totalTrackedTopics: 0,
        topicChart: [],
        records: [],
      });
    }
  );
};

/**
 * Subscribe to per-student subject monthly AI budgets ($0.30 cap specification)
 */
export const subscribeToStudentBudgets = (callback) => {
  const q = query(collection(db, 'student_ai_budgets'));

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        seedInitialAiData();
      }

      let totalSpentAllStudents = 0;
      let totalTokensAllStudents = 0;
      let cappedCount = 0;
      let warningCount = 0;
      let activeBudgetsCount = 0;

      const records = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const spent = Number(data.totalSpentUSD || 0);
        const limit = Number(data.budgetLimitUSD || 0.30);
        const tokens = Number(data.totalTokens || 0);
        const isCapped = spent >= limit;
        const isNearCap = !isCapped && (spent >= 0.25 || (spent / limit) >= 0.80);

        totalSpentAllStudents += spent;
        totalTokensAllStudents += tokens;
        activeBudgetsCount++;
        if (isCapped) cappedCount++;
        if (isNearCap) warningCount++;

        return {
          id: docSnap.id,
          ...data,
          totalSpentUSD: spent,
          budgetLimitUSD: limit,
          remainingUSD: Math.max(0, limit - spent),
          percentageUsed: limit > 0 ? Math.min(100, Math.round((spent / limit) * 100)) : 0,
          isCapped,
          isNearCap,
        };
      });

      callback({
        records,
        totalSpentAllStudents: Number(totalSpentAllStudents.toFixed(4)),
        totalTokensAllStudents,
        activeBudgetsCount,
        cappedCount,
        warningCount,
      });
    },
    (err) => {
      console.error('Error listening to student_ai_budgets:', err);
      callback({
        records: [],
        totalSpentAllStudents: 0,
        totalTokensAllStudents: 0,
        activeBudgetsCount: 0,
        cappedCount: 0,
        warningCount: 0,
      });
    }
  );
};

/**
 * Admin action: Update a student's budget limit for a subject (e.g. raise from $0.30 to $0.50)
 */
export const updateStudentBudgetLimit = async (budgetDocId, newLimitUSD) => {
  const budgetRef = doc(db, 'student_ai_budgets', budgetDocId);
  const limit = Number(newLimitUSD) || 0.30;
  await setDoc(
    budgetRef,
    {
      budgetLimitUSD: limit,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
};

/**
 * Admin action: Reset a student's monthly spent tokens and USD back to $0
 */
export const resetStudentBudget = async (budgetDocId) => {
  const budgetRef = doc(db, 'student_ai_budgets', budgetDocId);
  await setDoc(
    budgetRef,
    {
      totalSpentUSD: 0,
      totalTokens: 0,
      promptTokens: 0,
      candidateTokens: 0,
      interactionCount: 0,
      isNearCap: false,
      isCapped: false,
      resetAt: new Date().toISOString(),
    },
    { merge: true }
  );
};

/**
 * Real-time map of users (id -> { fullName, email, role, classLevel })
 */
export const subscribeToUsersMap = (callback) => {
  return onSnapshot(
    collection(db, 'users'),
    (snapshot) => {
      const map = {};
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() || {};
        map[docSnap.id] = {
          id: docSnap.id,
          fullName: data.fullName || data.name || data.displayName || '',
          email: data.email || '',
          role: data.role || 'student',
          classLevel: data.classLevel || '',
        };
      });
      callback(map);
    },
    (err) => {
      console.warn('Error subscribing to users map:', err);
      callback({});
    }
  );
};

